/*
 * CASA SOFIA — aside controller + document event bus.
 *
 * Ports the React <Aside> + Aside.Provider behaviour to a framework-free custom
 * element. ONE <aside-controller> lives in theme.liquid and wraps every drawer
 * overlay (cart, search, mobile menu). Drawer slices render their own overlay
 * markup inside it (see DOM CONTRACT below); this controller owns open/close,
 * Escape, scrim-click, scroll-lock and focus management for all of them.
 *
 * ── EVENT BUS (the public contract) ─────────────────────────────────────────
 *   Open a drawer:   document.dispatchEvent(new CustomEvent('aside:open',
 *                       { detail: { type: 'cart' | 'search' | 'mobile' } }))
 *   Close any drawer: document.dispatchEvent(new CustomEvent('aside:close'))
 *   Toggle helper:    document.dispatchEvent(new CustomEvent('aside:toggle',
 *                       { detail: { type } }))
 *
 *   The controller also re-broadcasts state so other slices can react:
 *     'aside:opened'  detail.type   — fired after a drawer is shown
 *     'aside:closed'  detail.type   — fired after the active drawer is hidden
 *
 *   Convenience triggers (no JS import needed in a slice): any element with
 *     data-aside-open="cart|search|mobile"   opens that drawer on click
 *     data-aside-close                        closes the active drawer on click
 *
 * ── DOM CONTRACT (what drawer slices must render inside <aside-controller>) ──
 *   <div class="overlay" data-aside-type="cart" role="dialog" aria-modal="true"
 *        aria-label="Cart" hidden>
 *     <button class="close-outside" tabindex="-1" aria-hidden="true"></button>
 *     <aside>
 *       <header>
 *         <h3>Cart</h3>
 *         <button class="close reset tap-target" data-aside-close
 *                 aria-label="Close">&times;</button>
 *       </header>
 *       <main> … drawer body … </main>
 *     </aside>
 *   </div>
 *
 *   • One .overlay per type. data-aside-type ∈ {'cart','search','mobile'}.
 *   • Open  = add class `expanded` (base.css animates opacity/transform).
 *   • The `.close-outside` button is the scrim; clicking it closes.
 *   • base.css already scroll-locks via `html:has(.overlay.expanded)`; this
 *     controller adds a `data-aside-open` attribute on <html> as a JS fallback
 *     for engines without :has(), and toggles each overlay's `hidden` attr so
 *     inert drawers stay out of the a11y tree / tab order.
 * ========================================================================= */

const ASIDE_TYPES = ['cart', 'search', 'mobile'];
const REDUCED_MOTION =
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

class AsideController extends HTMLElement {
  constructor() {
    super();
    this.activeType = null;
    this.lastFocused = null;
    this._onOpen = this._onOpen.bind(this);
    this._onClose = this._onClose.bind(this);
    this._onToggle = this._onToggle.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    // Event-bus listeners
    document.addEventListener('aside:open', this._onOpen);
    document.addEventListener('aside:close', this._onClose);
    document.addEventListener('aside:toggle', this._onToggle);
    // Delegated triggers + scrim/close-button clicks
    document.addEventListener('click', this._onClick);
    // Escape closes (global so triggers anywhere work)
    document.addEventListener('keydown', this._onKeydown);

    // Ensure every inert overlay starts hidden from AT / tab order.
    this._overlays().forEach((o) => {
      if (!o.classList.contains('expanded')) o.setAttribute('hidden', '');
    });
  }

  disconnectedCallback() {
    document.removeEventListener('aside:open', this._onOpen);
    document.removeEventListener('aside:close', this._onClose);
    document.removeEventListener('aside:toggle', this._onToggle);
    document.removeEventListener('click', this._onClick);
    document.removeEventListener('keydown', this._onKeydown);
    this._unlock();
  }

  /* ── public-ish API (also reachable via the document event bus) ────────── */
  open(type) {
    if (!ASIDE_TYPES.includes(type)) return;
    const overlay = this._overlay(type);
    if (!overlay) return;

    // Remember focus to restore on close (skip if we're switching drawers).
    if (!this.activeType) {
      this.lastFocused =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    } else if (this.activeType !== type) {
      this._hide(this._overlay(this.activeType));
    }

    this.activeType = type;
    overlay.removeAttribute('hidden');
    // Force a reflow so the transition runs even right after un-hiding.
    void overlay.offsetWidth;
    overlay.classList.add('expanded');
    this._lock();
    this._focusDrawer(overlay);

    document.dispatchEvent(
      new CustomEvent('aside:opened', {detail: {type}}),
    );
  }

  close() {
    if (!this.activeType) return;
    const type = this.activeType;
    const overlay = this._overlay(type);
    this._hide(overlay);
    this.activeType = null;
    this._unlock();
    this._restoreFocus();
    document.dispatchEvent(
      new CustomEvent('aside:closed', {detail: {type}}),
    );
  }

  toggle(type) {
    if (this.activeType === type) this.close();
    else this.open(type);
  }

  /* ── internal helpers ──────────────────────────────────────────────────── */
  _overlays() {
    return Array.from(this.querySelectorAll('.overlay[data-aside-type]'));
  }
  _overlay(type) {
    return this.querySelector(`.overlay[data-aside-type="${type}"]`);
  }

  _hide(overlay) {
    if (!overlay) return;
    overlay.classList.remove('expanded');
    const finalize = () => overlay.setAttribute('hidden', '');
    if (REDUCED_MOTION) {
      finalize();
    } else {
      // Hide only after the slide-out finishes (mirrors the aside transition).
      const onEnd = (e) => {
        if (e.target !== overlay) return;
        overlay.removeEventListener('transitionend', onEnd);
        if (!overlay.classList.contains('expanded')) finalize();
      };
      overlay.addEventListener('transitionend', onEnd);
      // Safety net in case transitionend never fires.
      window.setTimeout(() => {
        if (!overlay.classList.contains('expanded')) finalize();
      }, 700);
    }
  }

  _focusDrawer(overlay) {
    const drawer = overlay.querySelector('aside');
    if (!drawer) return;
    // Prefer the close button, then the first focusable element, then the
    // drawer itself (made focusable via tabindex if needed).
    const target =
      overlay.querySelector('[data-aside-close]') ||
      overlay.querySelector(
        'input, textarea, select, a[href], button:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
      ) ||
      drawer;
    if (target === drawer && !drawer.hasAttribute('tabindex')) {
      drawer.setAttribute('tabindex', '-1');
    }
    // Wait a tick so the element is visible/focusable after un-hiding.
    window.requestAnimationFrame(() => {
      try {
        target.focus({preventScroll: true});
      } catch (_) {
        /* no-op */
      }
    });
  }

  _restoreFocus() {
    if (this.lastFocused && document.contains(this.lastFocused)) {
      try {
        this.lastFocused.focus({preventScroll: true});
      } catch (_) {
        /* no-op */
      }
    }
    this.lastFocused = null;
  }

  _lock() {
    // base.css locks via html:has(.overlay.expanded); this attr is the JS
    // fallback for engines without :has() and a hook for any slice that needs it.
    document.documentElement.setAttribute('data-aside-open', this.activeType);
  }
  _unlock() {
    document.documentElement.removeAttribute('data-aside-open');
  }

  /* ── event handlers ─────────────────────────────────────────────────────── */
  _onOpen(e) {
    this.open(e?.detail?.type);
  }
  _onClose() {
    this.close();
  }
  _onToggle(e) {
    const type = e?.detail?.type;
    if (type) this.toggle(type);
  }
  _onKeydown(e) {
    if (e.key === 'Escape' && this.activeType) {
      e.preventDefault();
      this.close();
    }
  }
  _onClick(e) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    // Convenience open trigger
    const opener = t.closest('[data-aside-open]');
    if (opener && this.contains(opener) === false) {
      e.preventDefault();
      this.open(opener.getAttribute('data-aside-open'));
      return;
    }
    if (opener) {
      e.preventDefault();
      this.open(opener.getAttribute('data-aside-open'));
      return;
    }

    // Explicit close trigger (the header × button) or scrim
    if (t.closest('[data-aside-close]') || t.closest('.close-outside')) {
      e.preventDefault();
      this.close();
    }
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('aside-controller')) {
  customElements.define('aside-controller', AsideController);
}

/* Tiny helper so slices can open drawers without touching the bus directly. */
export function openAside(type) {
  document.dispatchEvent(new CustomEvent('aside:open', {detail: {type}}));
}
export function closeAside() {
  document.dispatchEvent(new CustomEvent('aside:close'));
}
